import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { DataTable } from '../../common/admin/shared/DataTable';
import { IBulletinTableRow, IBulletinListResponse } from '@/types/admin/bulletins';
import { IColumnConfig, IPaginationModel } from '@/types/admin/dataTable';
import { formatBulletinDate, formatBulletinTime, getBulletinPriority } from '@/utils/admin/bulletinUtils';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';

interface BulletinDataTableProps {
  data: IBulletinListResponse;
  isLoading: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  filterModel?: any;
  onFilterChange?: (filter: any) => void;
  sortModel?: any[];
  onSortChange?: (sort: any[]) => void;
  paginationModel?: IPaginationModel;
  onPaginationChange?: (pagination: IPaginationModel) => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onBulletinSelect: (bulletin: IBulletinTableRow) => void;
  updateCsvData?: (data: IBulletinTableRow[]) => void;
}

export const BulletinDataTable: React.FC<BulletinDataTableProps> = ({
  data,
  isLoading,
  searchQuery,
  onSearchChange,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  paginationModel,
  onPaginationChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onBulletinSelect,
  updateCsvData,
}) => {
  const { redesign, semantic } = useAdminColors();

  const bulletinColumns = useMemo<IColumnConfig<IBulletinTableRow>[]>(
    () => [
      {
        key: 'title',
        label: 'SUBJECT',
        width: '30%',
        sortable: true,
        filterable: true,
        renderCell: (value: any, row: IBulletinTableRow) => (
          <View style={styles.titleCell}>
            <View style={styles.titleRow}>
              <Text style={[styles.titleText, { color: redesign.text.primary }]} numberOfLines={1}>
                {row.title}
              </Text>
              {getBulletinPriority({
                highlight: row.highlight,
                pinHomepage: row.pinHomepage,
              } as any) === 'high' && (
                <View style={styles.priorityIndicator}>
                  <SVG_COMPONENTS.CHEVRON_RIGHT
                    width={12}
                    height={12}
                    color={semantic.status.warning}
                  />
                </View>
              )}
            </View>
            {row.recipientType !== 'all' && (
              <Text style={[styles.recipientType, { color: redesign.text.muted }]}>
                {row.recipientType === 'notice' ? 'Lease Notice' : row.recipientType}
              </Text>
            )}
          </View>
        ),
      },
      {
        key: 'date',
        label: 'SENT DATE',
        width: '15%',
        sortable: true,
        filterable: false,
        renderCell: (value: any, row: IBulletinTableRow) => (
          <Text style={[styles.cellText, { color: redesign.text.primary }]}>
            {formatBulletinDate(row.date)}
          </Text>
        ),
      },
      {
        key: 'time',
        label: 'SENT TIME',
        width: '15%',
        sortable: false,
        filterable: false,
        renderCell: (value: any, row: IBulletinTableRow) => (
          <Text style={[styles.cellText, { color: redesign.text.primary }]}>
            {formatBulletinTime(row.time)}
          </Text>
        ),
      },
      {
        key: 'messageReadCount',
        label: 'READ',
        width: '10%',
        sortable: true,
        filterable: false,
        renderCell: (value: any, row: IBulletinTableRow) => (
          <Text style={[styles.numberText, { color: redesign.text.primary }]}>
            {row.messageReadCount}
          </Text>
        ),
      },
      {
        key: 'pushNotificationSentCount',
        label: 'DELIVERED',
        width: '15%',
        sortable: true,
        filterable: false,
        renderCell: (value: any, row: IBulletinTableRow) => (
          <Text style={[styles.numberText, { color: redesign.text.primary }]}>
            {row.pushNotificationSentCount}
          </Text>
        ),
      },
      {
        key: 'messageSentCount',
        label: 'RECIPIENTS',
        width: '15%',
        sortable: true,
        filterable: false,
        renderCell: (value: any, row: IBulletinTableRow) => (
          <Text style={[styles.numberText, { color: redesign.text.primary }]}>
            {row.messageSentCount}
          </Text>
        ),
      },
    ],
    [redesign.text.primary, redesign.text.muted, semantic.status.warning],
  );

  return (
    <DataTable<IBulletinTableRow>
      data={{
        rows: data.data.map((bulletin) => ({
          id: bulletin.id,
          title: bulletin.title,
          subject: bulletin.subject || bulletin.title,
          date: bulletin.createdAt ? new Date(bulletin.createdAt) : null,
          time: bulletin.createdAt ? new Date(bulletin.createdAt) : null,
          messageSentCount: bulletin.messageSentCount,
          messageReadCount: bulletin.messageReadCount,
          pushNotificationSentCount: bulletin.pushNotificationSentCount,
          highlight: bulletin.highlight,
          pinHomepage: bulletin.pinHomepage,
          publishType: bulletin.publishType,
          recipientType: bulletin.recipientType,
        })),
        count: data.count,
      }}
      columns={bulletinColumns}
      isLoading={isLoading}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search bulletins..."
      filterModel={filterModel}
      onFilterChange={onFilterChange}
      sortModel={sortModel}
      onSortChange={onSortChange}
      paginationModel={paginationModel}
      onPaginationChange={onPaginationChange}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
      onRowSelect={onBulletinSelect}
      updateCsvData={updateCsvData}
      emptyStateType="bulletins"
      mobileColumnCount={2}
    />
  );
};

const styles = StyleSheet.create({
  titleCell: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleText: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  priorityIndicator: {
    flexShrink: 0,
  },
  recipientType: {
    fontSize: 12,
    marginTop: 2,
    fontStyle: 'italic',
  },
  cellText: {
    fontSize: 14,
  },
  numberText: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
});
